import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import './DashboardApprovalPanel.css';
import './DashboardLeaveMonthPanel.css';

function formatPeriod(start, end) {
  const s = String(start || '').slice(0, 10);
  const e = String(end || '').slice(0, 10);
  if (!s) return '—';
  if (!e || s === e) return s;
  return `${s} ~ ${e}`;
}

function getDaysLabel(n) {
  if (n == null || Number.isNaN(Number(n))) return '';
  const x = Number(n);
  if (x % 1 === 0) return `${Math.trunc(x)}일`;
  return `${x}일`;
}

function ymdSlice(x) {
  return String(x || '').slice(0, 10);
}

function localYmd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** 오늘·내일 휴가 여부 → 행 강조 클래스 */
function leaveUrgencyClass(start, end) {
  const s = ymdSlice(start);
  const e = ymdSlice(end || start);
  if (!s) return '';
  const now = new Date();
  const today = localYmd(now);
  const t2 = new Date(now);
  t2.setDate(t2.getDate() + 1);
  const tomorrow = localYmd(t2);
  if (today >= s && today <= e) return 'dlm-line-item--off-today';
  if (tomorrow >= s && tomorrow <= e) return 'dlm-line-item--off-tomorrow';
  return '';
}

function leaveUrgencyRank(start, end) {
  const c = leaveUrgencyClass(start, end);
  if (c === 'dlm-line-item--off-today') return 0;
  if (c === 'dlm-line-item--off-tomorrow') return 1;
  return 2;
}

const LEAVE_TYPE_VISUAL = {
  annual: { emoji: '🏖️', pill: 'dlm-type-pill--annual' },
  half_am: { emoji: '🌤️', pill: 'dlm-type-pill--half' },
  half_pm: { emoji: '🌆', pill: 'dlm-type-pill--half' },
  sick: { emoji: '🤒', pill: 'dlm-type-pill--sick' },
  special: { emoji: '✨', pill: 'dlm-type-pill--special' },
  public: { emoji: '🏛️', pill: 'dlm-type-pill--public' },
};

function leaveTypeVisual(leaveType) {
  const k = String(leaveType || '').trim().toLowerCase();
  return LEAVE_TYPE_VISUAL[k] || { emoji: '📋', pill: 'dlm-type-pill--default' };
}

/**
 * 결재 패널(dap-panel)과 동일 폭·톤. 오늘부터 30일 안에 시작하는 승인 휴가, 한 줄씩.
 */
export default function DashboardLeaveMonthPanel({ token, stackBelowApproval, refreshToken = 0 }) {
  const apiUrl = getApiBaseUrl();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [windowLabel, setWindowLabel] = useState('');

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token],
  );

  const load = useCallback(async () => {
    if (!token) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr('');
    try {
      const res = await fetch(
        `${apiUrl}/api/erp_leave/upcoming_approved_window?days=30&_=${Date.now()}`,
        { credentials: 'include', headers: { ...authHeaders } },
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data || data.success !== true) {
        const msg = (data && (data.detail || data.message)) || `조회 실패 (${res.status})`;
        throw new Error(String(msg));
      }
      setItems(Array.isArray(data.items) ? data.items : []);
      const a = String(data.window_start || '').slice(0, 10);
      const b = String(data.window_end || '').slice(0, 10);
      setWindowLabel(a && b ? `${a} ~ ${b}` : '');
    } catch (e) {
      setErr(String(e?.message || e));
      setItems([]);
      setWindowLabel('');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshToken) return;
    load();
  }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedItems = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : [];
    arr.sort((a, b) => {
      const ra = leaveUrgencyRank(a.start_date, a.end_date);
      const rb = leaveUrgencyRank(b.start_date, b.end_date);
      if (ra !== rb) return ra - rb;
      return String(ymdSlice(a.start_date)).localeCompare(String(ymdSlice(b.start_date)));
    });
    return arr;
  }, [items]);

  const panelClass = stackBelowApproval ? 'dap-panel dlm-under-approval' : 'dap-panel';

  return (
    <section className={panelClass} aria-label="다가오는 승인 휴가 전 유형">
      <div className="dap-panel__head">
        <h3 className="dap-panel__title">다가오는 휴가</h3>
        <button type="button" className="dap-panel__refresh" onClick={() => load()} disabled={loading}>
          새로고침
        </button>
      </div>
      <p className="dap-panel__sub">
        승인 완료·오늘부터 30일 안에 기간이 겹치는 일정입니다.{' '}
        <span className="dlm-legend">
          <span className="dlm-legend__today">오늘 휴무</span>·
          <span className="dlm-legend__tomorrow">내일 휴무</span> 행으로 구분됩니다.
        </span>
        {windowLabel ? (
          <>
            {' '}
            <span className="dlm-window-range">({windowLabel})</span>
          </>
        ) : null}
      </p>

      {err ? <div className="dap-panel__err">{err}</div> : null}
      {loading ? <div className="dap-panel__muted">불러오는 중…</div> : null}

      {!loading && !err && items.length === 0 ? (
        <div className="dap-panel__empty">표시할 일정이 없습니다.</div>
      ) : null}

      {!loading && !err && items.length > 0 ? (
        <ul className="dap-list dlm-line-list">
          {sortedItems.map((it, idx) => {
            const urgency = leaveUrgencyClass(it.start_date, it.end_date);
            const { emoji, pill } = leaveTypeVisual(it.leave_type);
            const typeLabel = String(it.type_label || it.leave_type || '휴가').trim();
            return (
              <li
                key={`${it.email}-${it.start_date}-${it.end_date}-${it.leave_type}-${idx}`}
                className={`dlm-line-item ${urgency}`.trim()}
                title={it.reason ? String(it.reason) : undefined}
              >
                {urgency === 'dlm-line-item--off-today' ? (
                  <span className="dlm-chip dlm-chip--today" title="오늘 휴무">
                    오늘
                  </span>
                ) : null}
                {urgency === 'dlm-line-item--off-tomorrow' ? (
                  <span className="dlm-chip dlm-chip--tomorrow" title="내일 휴무">
                    내일
                  </span>
                ) : null}
                <span className="dlm-line-name">{String(it.display_name || it.email || '').trim()}</span>
                <span className="dlm-line-sep">·</span>
                <span className={`dlm-type-pill ${pill}`}>
                  <span className="dlm-type-pill__emoji" aria-hidden>
                    {emoji}
                  </span>
                  {typeLabel}
                </span>
                <span className="dlm-line-sep">·</span>
                <span className="dlm-line-period">{formatPeriod(it.start_date, it.end_date)}</span>
                <span className="dlm-line-sep">·</span>
                <span className="dlm-line-days">
                  {it.leave_type === 'public' ? '연차 미차감' : getDaysLabel(it.use_days)}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
